import { Button, IconButton, Tooltip } from "@mui/material";
import { eventClick, RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import useKothar from "context/useKothar";
import DOMPurify from "dompurify";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaPlusCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { hasPermission } from "utils/permissionUtils";
import { highlightHtml, stripHtml } from "utils/utils";
import AddPriceDrawer from "./AddPriceDrawer";
import ViewPriceListModal from "./ViewPriceListModal";
import { deleteData } from "const/axios";
import { getFileIcon } from "utils/utils";
import { P } from "const/permissions";

const PriceList = ({ color = "light", readOnly = false, embedded = false }) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [{ priceList, token, isPriceListLoading }, { refetchPriceList }] = useKothar();
  const [openAddPriceDrawer, setOpenAddPriceDrawer] = useState({
    state: false,
    id: null,
    data: null,
  });
  const [openViewModal, setOpenViewModal] = useState({
    state: false,
    data: null,
  });
  const canManage = !readOnly && hasPermission(P.PRICELIST.CREATE);

  useEffect(() => {
    if (!priceList?.length) {
      refetchPriceList();
    }
  }, []);

  const handleDeleteData = () => {
    deleteData(
      `pricelists/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchPriceList();
      },
      token
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "module",
        header: "Module",
        size: 200,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue}>
            <div className="flex items-center">
              <div className=" mr-2">{renderedCellValue}</div>
            </div>
          </Tooltip>
        ),
      },
      {
        accessorKey: "minimumServiceCharge",
        header: "Minimum Service Charge",
        size: 200,

        Cell: ({ renderedCellValue }) => {
          return <div className="whitespace-pre-line">{renderedCellValue}</div>;
        },
      },
      {
        accessorKey: "maximumServiceCharge",
        header: "Maximum Service Charge",
        size: 150,
      },
      {
        accessorKey: "normalServiceCharge",
        header: "Normal Service Charge",
        size: 150,
      },
      {
        accessorKey: "governmentFees",
        header: "Government Fees",
        size: 150,
      },

      // {
      //   accessorKey: "requirements",
      //   header: "Checklist",
      //   size: 150,
      //   filterFn: (row, columnId, filterValue) => {
      //     const text = stripHtml(row.getValue(columnId));
      //     return text.toLowerCase().includes(filterValue.toLowerCase());
      //   },

      //   Cell: ({ cell, table }) => {
      //     const rawHtml = cell.getValue()?.slice(0, 200) ?? "";

      //     // Get MRT global search value
      //     const searchValue = table.getState().globalFilter;

      //     const highlightedHtml = highlightHtml(rawHtml, searchValue);

      //     return (
      //       <div
      //         dangerouslySetInnerHTML={{
      //           __html: DOMPurify.sanitize(highlightedHtml),
      //         }}
      //         className="max-w-[200px] truncate"
      //       />
      //     );
      //   },
      // },
      {
        accessorKey: "key4",
        header: "Attachments",
        size: 150,
        Cell: ({ renderedCellValue }) => (
          <div className="flex flex-wrap gap-2">
            {renderedCellValue?.length > 0
              ? renderedCellValue
                  ?.filter((item) => item !== "")
                  ?.map((item, index) => (
                    <Tooltip key={item || index} title={item} arrow>
                      <a
                        href={item}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 rounded border border-app-border hover:bg-app-surface-hover transition-colors"
                      >
                        {getFileIcon(item)}
                      </a>
                    </Tooltip>
                  ))
              : "-"}
          </div>
        ),
      },

      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => {
          if (!canManage) return null;
          return (
            <div className="flex items-center">
              <Tooltip title="Edit Price List" arrow>
                <IconButton
                  onClick={() =>
                    setOpenAddPriceDrawer({
                      state: true,
                      id: row?.original?.id,
                      data: row?.original,
                    })
                  }
                >
                  <AiFillEdit className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Price List" arrow>
                <IconButton
                  onClick={() =>
                    setOpenConfirmationModal({
                      state: true,
                      id: row?.original?.id,
                    })
                  }
                >
                  <AiFillDelete className="text-red-600 cursor-pointer" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [canManage]
  );

  const table = useMaterialReactTable({
    columns: readOnly ? columns.filter((col) => col.accessorKey !== "actions") : columns,
    data: priceList,
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh", // Set max height
        minHeight: "70vh", // Set max height
        overflowY: "auto", // Enable vertical scrolling
      },
    },
    state: {
      isLoading: isPriceListLoading, //cell skeletons and loading overlay
      showProgressBars: isPriceListLoading, //progress bars while refetching
      isSaving: isPriceListLoading, //progress bars and save button spinners
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Check if the click was on an action button or its child

        if (!eventClick(event)) {
          setOpenViewModal({
            state: true,
            data: row?.original,
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className={embedded ? "w-full" : "flex flex-wrap mt-4 dashBody"}>
      <div className={embedded ? "w-full" : "w-full mb-12 px-4"}>
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                Price List ({priceList?.length}) <RefreshData refreshData={refetchPriceList} />
              </h3>

              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  onClick={() =>
                    setOpenAddPriceDrawer({
                      state: true,
                      id: null,
                      data: null,
                    })
                  }
                >
                  Add Price List
                </Button>
              )}
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {!readOnly && openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Price List"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => handleDeleteData()}
        />
      )}
      {!readOnly && (
        <AddPriceDrawer
          open={openAddPriceDrawer}
          handleClose={() =>
            setOpenAddPriceDrawer({
              state: false,
              id: null,
              data: null,
            })
          }
          setOpen={setOpenAddPriceDrawer}
          refetchPriceList={refetchPriceList}
        />
      )}
      <ViewPriceListModal
        open={openViewModal.state}
        handleClose={() =>
          setOpenViewModal({
            state: false,
            data: null,
          })
        }
        data={openViewModal.data}
        onEdit={
          openViewModal.data && canManage
            ? () => {
                setOpenViewModal({ state: false, data: null });
                setOpenAddPriceDrawer({
                  state: true,
                  id: openViewModal.data?.id,
                  data: openViewModal.data,
                });
              }
            : null
        }
      />
    </div>
  );
};

export default PriceList;
